import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { AlertTriangle, CheckCircle2, Trash2, UserX, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const CATEGORY_LABELS = {
  abuse: 'Abuse',
  user_request: 'User Request',
  non_payment: 'Non-Payment',
  other: 'Other',
};
const CATEGORY_COLORS = {
  abuse: 'bg-red-100 text-red-700 border-red-200',
  user_request: 'bg-blue-50 text-blue-700 border-blue-200',
  non_payment: 'bg-amber-50 text-amber-700 border-amber-200',
  other: 'bg-bone/60 text-text-muted border-black/10',
};

const DELETE_REASONS = [
  { value: 'non_payment', label: 'Non-payment' },
  { value: 'user_request', label: 'User request' },
  { value: 'abuse', label: 'Abuse / TOS' },
  { value: 'duplicate', label: 'Duplicate account' },
  { value: 'other', label: 'Other' },
];

export default function FlagsPanel({ onChange }) {
  const [flags, setFlags] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [status, setStatus] = useState('open');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(null); // flag being resolved

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status, limit: '100' });
      if (category) params.set('category', category);
      const res = await api.get(`/admin/flags?${params}`);
      setFlags(res.data.flags || []);
      setOpenCount(res.data.open_count || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load flags');
    } finally {
      setLoading(false);
    }
  }, [status, category]);

  useEffect(() => { load(); }, [load]);

  const quickResolve = async (flag, resolution) => {
    try {
      await api.post(`/admin/flags/${flag.flag_id}/resolve`, { resolution });
      toast.success(`Flag ${resolution}`);
      load();
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resolve failed');
    }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div className="mystical-card p-6" data-testid="admin-flags-panel">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-red-600" />
          <span className="text-red-700 font-sans text-xs tracking-[0.25em] uppercase">Account Flags</span>
          {openCount > 0 && (
            <Badge className="bg-red-100 text-red-700 border border-red-200 text-[10px] font-sans">
              {openCount} open
            </Badge>
          )}
        </div>
        <div className="flex gap-2">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="flags-status-select"
            className="h-9 px-3 bg-sand/50 border border-black/8 rounded-lg font-sans text-xs outline-none cursor-pointer"
          >
            <option value="open">Open</option>
            <option value="reviewed">Reviewed</option>
            <option value="all">All</option>
          </select>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="flags-category-select"
            className="h-9 px-3 bg-sand/50 border border-black/8 rounded-lg font-sans text-xs outline-none cursor-pointer"
          >
            <option value="">All categories</option>
            {Object.entries(CATEGORY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
        {loading && <p className="font-sans text-sm text-text-muted text-center py-8">Loading...</p>}
        {!loading && flags.length === 0 && (
          <p className="font-sans text-sm text-text-muted text-center py-8" data-testid="flags-empty">
            {status === 'open' ? 'No open flags — all clear.' : 'No flags match'}
          </p>
        )}
        {!loading && flags.map((f) => (
          <div
            key={f.flag_id}
            className={`p-4 rounded-xl border ${f.reviewed ? 'bg-bone/40 border-black/8 opacity-70' : 'bg-red-50/20 border-red-200/40'}`}
            data-testid={`flag-row-${f.flag_id}`}
          >
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge className={`${CATEGORY_COLORS[f.category] || CATEGORY_COLORS.other} border text-[10px] font-sans`}>
                {CATEGORY_LABELS[f.category] || f.category}
              </Badge>
              {f.auto_paused && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-300 border text-[10px] font-sans">
                  Auto-paused
                </Badge>
              )}
              {f.reviewed && (
                <Badge className="bg-moss/15 text-moss border-moss/25 border text-[10px] font-sans">
                  Resolved → {f.resolution}
                </Badge>
              )}
              <span className="font-sans text-[10px] text-text-muted ml-auto">{fmt(f.created_at)}</span>
            </div>

            <p className="font-serif text-base text-dark">
              {f.user?.email || '(user removed)'}
              {f.user?.name && <span className="text-text-muted font-sans text-xs ml-2">({f.user.name})</span>}
            </p>
            <p className="font-mono text-[11px] text-text-muted mt-1">
              <span className="uppercase tracking-wider mr-2">Signal</span>{f.auto_signal}
            </p>
            {f.notes && (
              <p className="font-sans text-xs text-text-muted mt-2 italic bg-bone/40 p-2 rounded-lg border border-black/5">
                "{f.notes}"
              </p>
            )}
            <p className="font-sans text-[10px] text-text-muted/70 mt-2">
              triggered by: {f.triggered_by}
            </p>

            {!f.reviewed && (
              <div className="flex flex-wrap gap-2 mt-3">
                <Button
                  onClick={() => quickResolve(f, 'unpaused')}
                  size="sm"
                  variant="outline"
                  data-testid={`flag-unpause-${f.flag_id}`}
                  className="h-9 px-3 border-moss/30 text-moss bg-moss/5 hover:bg-moss/10 rounded-lg font-sans text-[10px] tracking-wider uppercase"
                >
                  <CheckCircle2 size={12} className="mr-1.5" /> Unpause & dismiss
                </Button>
                <Button
                  onClick={() => quickResolve(f, 'dismissed')}
                  size="sm"
                  variant="outline"
                  data-testid={`flag-dismiss-${f.flag_id}`}
                  className="h-9 px-3 border-black/15 bg-bone/40 hover:bg-bone rounded-lg font-sans text-[10px] tracking-wider uppercase"
                >
                  Dismiss
                </Button>
                <Button
                  onClick={() => setResolving(f)}
                  size="sm"
                  variant="outline"
                  data-testid={`flag-delete-${f.flag_id}`}
                  className="h-9 px-3 border-red-300 text-red-700 bg-red-50/40 hover:bg-red-50 rounded-lg font-sans text-[10px] tracking-wider uppercase"
                >
                  <Trash2 size={12} className="mr-1.5" /> Delete user
                </Button>
              </div>
            )}
            {f.reviewed && (
              <p className="font-sans text-[10px] text-text-muted/60 mt-2">
                Resolved by {f.resolved_by} on {fmt(f.resolved_at)}
                {f.resolution_notes && <span> — {f.resolution_notes}</span>}
              </p>
            )}
          </div>
        ))}
      </div>

      {resolving && (
        <ResolveDeleteModal
          flag={resolving}
          onClose={() => setResolving(null)}
          onResolved={() => {
            setResolving(null);
            load();
            onChange && onChange();
          }}
        />
      )}
    </div>
  );
}

function ResolveDeleteModal({ flag, onClose, onResolved }) {
  const [reason, setReason] = useState(flag.category === 'abuse' ? 'abuse' : flag.category === 'user_request' ? 'user_request' : 'other');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/admin/flags/${flag.flag_id}/resolve`, {
        resolution: 'soft_deleted',
        delete_reason: reason,
        delete_notes: notes.trim() || undefined,
      });
      toast.success('User deleted. 30-day grace window started.');
      onResolved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="flag-resolve-modal"
      style={{ background: 'rgba(15,16,16,0.55)', backdropFilter: 'blur(8px)' }}
      onClick={() => !submitting && onClose()}
    >
      <div
        className="bg-bone rounded-t-3xl sm:rounded-3xl p-7 sm:p-8 w-full sm:max-w-lg relative animate-fade-up"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
      >
        <p className="text-red-700 font-sans text-[10px] tracking-[0.35em] uppercase mb-2">Soft-Delete From Flag</p>
        <h3 className="font-serif text-2xl font-light text-dark leading-tight mb-2 mystical-heading">
          Delete {flag.user?.email || 'this user'}?
        </h3>
        <p className="font-sans text-sm text-text-muted leading-relaxed mb-5">
          30-day grace window applies. The flag will be marked resolved.
        </p>
        <label className="block mb-4">
          <span className="font-sans text-xs text-text-muted tracking-wider uppercase">Reason</span>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="flag-delete-reason"
            className="mt-2 w-full h-11 px-3 bg-sand/50 border border-black/8 focus:border-gold rounded-xl font-sans text-sm outline-none cursor-pointer"
          >
            {DELETE_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </label>
        <label className="block mb-5">
          <span className="font-sans text-xs text-text-muted tracking-wider uppercase">Notes (optional)</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 500))}
            data-testid="flag-delete-notes"
            placeholder="Audit log context..."
            rows={3}
            className="mt-2 w-full px-3 py-2 bg-sand/50 border border-black/8 focus:border-gold rounded-xl font-sans text-sm outline-none resize-none"
          />
        </label>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting} className="font-sans text-xs text-text-muted h-11">Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="flag-delete-confirm"
            className="bg-red-600 hover:bg-red-700 text-white rounded-xl font-sans text-xs tracking-[0.25em] uppercase h-11 px-6 disabled:opacity-40"
          >
            <UserX size={14} className="mr-2" />
            {submitting ? 'Deleting...' : 'Delete user'}
          </Button>
        </div>
      </div>
    </div>
  );
}
